import { useMemo, useState } from 'react'
import { checkStructure } from 'src/endpoints'
import styled, { css } from 'styled-components'

import { Container, LightTable, Loader } from '@/components'
import { useApi } from '@/endpoints/hooks'
import { ValidationMessageDto as ValidationMessageDtoOld } from '@/endpoints/models'
import { useAppContext } from '@/hooks'
import {
	StructureDto,
	ValidationMessageDto,
} from '@/rtkApi/mddApiSliceGenerated'
import { LightTableColumn } from '@/types'

import { Filter, FilterValues } from './Filter'
import { filterValidationData } from './helpers'

type ValidationProps = {
	node: StructureDto
}

export const Validation = ({ node }: ValidationProps) => {
	const { t } = useAppContext()
	const validation = useApi(checkStructure(node.id as number))

	const [filter, setFilter] = useState<FilterValues>({})

	const filteredData = useMemo(
		() => filterValidationData(validation.data || [], filter),
		[validation.data, filter],
	)

	const model = useMemo(
		(): LightTableColumn<ValidationMessageDto>[] => [
			{
				field: 'id',
				title: t('VALIDATION_ID'),
				width: 5,
			},
			{
				field: 'severity',
				title: t('VALIDATION_SEVERITY'),
				width: 10,
				formatter: (value) => (
					<SeverityBadge
						$severity={value as ValidationMessageDtoOld.SeverityEnum}
					>
						{value}
					</SeverityBadge>
				),
			},
			{
				field: 'nodeName',
				title: t('VALIDATION_ELEMENT'),
				width: 80,
				formatter: (_, row) => `${row.nodeName} (${row.nodeType})`,
			},
			{
				field: 'elementName',
				title: t('VALIDATION_ATTRIBUTE'),
				width: 80,
				formatter: (_, row) => `${row.elementName} (${row.elementType})`,
			},
			{
				field: 'message',
				title: t('VALIDATION_MESSAGE'),
				width: 200,
			},
			{
				field: 'description',
				title: t('VALIDATION_DESCRIPTION'),
				width: 200,
			},
		],
		[t],
	)

	return (
		<Container>
			<Filter onChange={(values) => setFilter(values)} />

			{validation.loading ? (
				<Loader loaded={false} />
			) : (
				<LightTable
					keyField="id"
					isLoading={validation.reloading}
					data={filteredData}
					columns={model}
				/>
			)}
		</Container>
	)
}

const SeverityBadge = styled.span<{
	$severity: ValidationMessageDtoOld.SeverityEnum
}>`
	display: inline-block;
	color: white;
	padding: 3px 5px;

	${(props) =>
		props.$severity === ValidationMessageDtoOld.SeverityEnum.ERROR &&
		css`
			background: ${props.theme.colors.danger.base};
		`};

	${(props) =>
		props.$severity === ValidationMessageDtoOld.SeverityEnum.WARN &&
		css`
			background: ${props.theme.colors.warn.base};
		`};

	${(props) =>
		props.$severity === ValidationMessageDtoOld.SeverityEnum.INFO &&
		css`
			background: ${props.theme.colors.info.light};
		`};
`
